/**
 * Connection-scoped Designer themes → App theme registry.
 *
 * Only themes with `themeData` on Designer `theme` nodes are registered as
 * `source: 'config'`. Builtins stay in the registry for shell/login fallback
 * but never appear in Theme Studio's availableThemes list.
 */
import type { ConfigNode } from '@/designer/types/config.types'
import type { AppTheme } from '@/designer/types/theme.types'
import { appThemeToMui } from '@/designer/utils/appThemeToMui'
import type { ThemeConfig } from './types'
import {
  getAllThemes,
  getThemeById,
  registerTheme,
  unregisterTheme,
  getDefaultTheme,
} from './registry'
import { getStoredThemeId, setStoredThemeId } from './config'

export interface ThemeNodeWithData {
  id: string
  name: string
  isDefault?: boolean
  themeData: AppTheme
}

/** Walk the designer tree and collect theme nodes that carry a full payload. */
export function extractThemeNodes(tree: ConfigNode[] | null | undefined): ThemeNodeWithData[] {
  if (!tree?.length) return []
  const out: ThemeNodeWithData[] = []
  const walk = (nodes: ConfigNode[]) => {
    for (const n of nodes) {
      if (n.type === 'theme' && n.themeData && typeof n.themeData === 'object') {
        out.push({
          id: n.id,
          name: n.name || n.themeData.name || n.id,
          isDefault: n.isDefault === true || n.themeData.isDefault === true,
          themeData: n.themeData,
        })
      }
      if (n.children?.length) walk(n.children)
    }
  }
  walk(tree)
  return out
}

export function appThemeToThemeConfig(appTheme: AppTheme): ThemeConfig {
  return {
    id: appTheme.id,
    name: appTheme.name,
    description: appTheme.description,
    source: 'config',
    createTheme: () => appThemeToMui(appTheme),
  }
}

/**
 * Replace every previously synced config theme with the ones from this tree.
 * Returns the list of registered config ThemeConfigs (may be empty).
 */
export function syncConnectionThemes(
  _connectionId: string | null,
  tree: ConfigNode[] | null | undefined,
): ThemeConfig[] {
  for (const t of getAllThemes()) {
    if (t.source === 'config') unregisterTheme(t.id)
  }

  const nodes = extractThemeNodes(tree)
  const registered: ThemeConfig[] = []
  for (const node of nodes) {
    const config = appThemeToThemeConfig({
      ...node.themeData,
      id: node.id,
      name: node.name,
      isDefault: node.isDefault === true,
    })
    registerTheme(config)
    registered.push(config)
  }
  return registered
}

export function defaultThemeIdsFromNodes(nodes: ThemeNodeWithData[]): string[] {
  return nodes.filter((n) => n.isDefault).map((n) => n.id)
}

/**
 * After a sync: keep stored theme if still valid (config in synced list, or
 * non-config platform theme). Else Designer default, else platform fallback.
 */
export function pickActiveThemeAfterSync(
  synced: ThemeConfig[],
  defaultThemeIds: string[],
): ThemeConfig {
  const stored = getStoredThemeId()
  if (stored) {
    const inSynced = synced.find((t) => t.id === stored)
    if (inSynced) return inSynced
    const registered = getThemeById(stored)
    if (registered && registered.source !== 'config') return registered
    // Stale config id — fall through to Designer default / platform
  }

  for (const id of defaultThemeIds) {
    const t = synced.find((x) => x.id === id)
    if (t) {
      setStoredThemeId(t.id)
      return t
    }
  }

  return getDefaultTheme()
}

/** Themes shown in Theme Studio / App picker — config only. */
export function getAvailableConfigThemes(): ThemeConfig[] {
  return getAllThemes().filter((t) => t.source === 'config')
}
